import axios from 'axios';

class AssignmentService {
    /**
     * Create a new video assignment
     * 
     * @param {Object} assignment - Assignment details 
     * @param {string} email - User email
     * @param {string} authToken - Authentication token
     * @returns {Promise} - Promise resolving to API response
     */
    async createAssignment(assignment, email, authToken) {
        // Make sure category is set
        if (!assignment.category) {
            assignment.category = 'Level 1'; // Default
        }

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/v3/assignments`,
                assignment,
                {
                    headers: {
                        email: email,
                        auth: authToken,
                        'Content-Type': 'application/json',
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Credentials': true,
                    }
                }
            );
            return response;
        } catch (error) {
            console.error('Error creating assignment:', error);
            throw error;
        }
    }

    /**
     * Get assignments assigned to the current user
     * 
     * @param {string} email - User email
     * @param {string} authToken - Authentication token
     * @returns {Promise} - Promise resolving to API response
     */
    async getMyAssignments(email, authToken) {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/v3/assignments/my-assignments`,
                {
                    headers: {
                        email: email,
                        auth: authToken,
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Credentials': true,
                    }
                }
            );
            return response;
        } catch (error) {
            console.error('Error fetching my assignments:', error);
            throw error;
        }
    }

    /**
     * Get assignments assigned by the current user
     * 
     * @param {string} email - User email
     * @param {string} authToken - Authentication token
     * @returns {Promise} - Promise resolving to API response
     */
    async getAssignedByMe(email, authToken) {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/v3/assignments/assigned-by-me`,
                {
                    headers: {
                        email: email,
                        auth: authToken,
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Credentials': true,
                    }
                }
            );
            return response;
        } catch (error) {
            console.error('Error fetching assignments assigned by me:', error);
            throw error;
        }
    }

    /**
     * Get all assignments (admin only)
     * 
     * @param {string} email - User email
     * @param {string} authToken - Authentication token
     * @returns {Promise} - Promise resolving to API response
     */
    async getAllAssignments(email, authToken) {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/v3/assignments`,
                {
                    headers: {
                        email: email,
                        auth: authToken,
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Credentials': true,
                    }
                }
            );
            return response;
        } catch (error) {
            console.error('Error fetching all assignments:', error);
            throw error;
        }
    }

    /**
     * Get assignments for a specific video
     * 
     * @param {string} videoId - Video ID
     * @param {string} email - User email
     * @param {string} authToken - Authentication token
     * @returns {Promise} - Promise resolving to API response
     */
    async getAssignmentsForVideo(videoId, email, authToken) {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/v3/assignments/video/${videoId}`,
                {
                    headers: {
                        email: email,
                        auth: authToken,
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Credentials': true,
                    }
                }
            );
            return response;
        } catch (error) {
            console.error('Error fetching assignments for video:', error);
            throw error;
        }
    }

    /**
     * Update assignment status
     * 
     * @param {string} assignmentId - Assignment ID
     * @param {string} status - New status
     * @param {string} email - User email
     * @param {string} authToken - Authentication token
     * @returns {Promise} - Promise resolving to API response
     */
    async updateAssignmentStatus(assignmentId, status, email, authToken) {
        try {
            // Ensure status is a clean string without extra quotes
            const cleanStatus = status.replace(/^"+|"+$/g, '');
            
            console.log('Updating assignment status:', assignmentId, 'to:', cleanStatus);
            
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/api/v3/assignments/${assignmentId}/status`,
                { status: cleanStatus }, // Use the cleaned status string
                {
                    headers: {
                        email: email,
                        auth: authToken,
                        'Content-Type': 'application/json',
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Credentials': true,
                    }
                }
            );
            return response;
        } catch (error) {
            console.error('Error updating assignment status:', error);
            throw error;
        }
    }

    
    /**
     * Update an assignment
     * 
     * @param {string} id - Assignment ID
     * @param {Object} assignment - Updated assignment details
     * @param {string} email - User email
     * @param {string} authToken - Authentication token
     * @returns {Promise} - Promise resolving to API response
     */
    async updateAssignment(id, assignment, email, authToken) {
        try {
            console.log('Updating assignment:', id, assignment);  // Add logging for debugging
            
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/api/v3/assignments/${id}`,
                assignment,  // Sending the assignment directly in the request body
                {
                    headers: {
                        'email': email,
                        'auth': authToken,
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response;
        } catch (error) {
            console.error('Error updating assignment:', error);
            throw error;
        }
    }

    /**
     * Delete an assignment
     * 
     * @param {string} assignmentId - Assignment ID
     * @param {string} email - User email
     * @param {string} authToken - Authentication token
     * @returns {Promise} - Promise resolving to API response
     */
    async deleteAssignment(assignmentId, email, authToken) {
        try {
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/v3/assignments/${assignmentId}`,
                {
                    headers: {
                        email: email,
                        auth: authToken,
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Credentials': true,
                    }
                }
            );
            return response;
        } catch (error) {
            console.error('Error deleting assignment:', error);
            throw error;
        }
    }

    /**
     * Get assignments by status
     * 
     * @param {string} status - Status to filter by
     * @param {string} email - User email
     * @param {string} authToken - Authentication token
     * @returns {Promise} - Promise resolving to API response
     */
    async getAssignmentsByStatus(status, email, authToken) {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/v3/assignments/status/${status}`,
                {
                    headers: {
                        email: email,
                        auth: authToken,
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Credentials': true,
                    }
                }
            );
            return response;
        } catch (error) {
            console.error('Error fetching assignments by status:', error);
            throw error;
        }
    }
}

export default new AssignmentService();